/*// src/pages/ChangeLogPage.jsx
import { useContext, useEffect, useState } from "react";
import { ServerContext } from "../../common/context/ServerContext";

export default function ChangeLogPage() {
  const { servidor } = useContext(ServerContext); // servidor = API_BASE_URL (ex: http://localhost:8000/api)

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function fetchLogs() {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${servidor}/api/changelogs/1`, {
        method: "GET",
        headers: {
          "Accept": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      setLogs(data);
    } catch (err) {
      console.error(err);
      setError("Erro ao carregar o changelog.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchLogs();
  }, [servidor]); // se a URL mudar, recarrega

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: 20 }}>
      <h1>Change Log</h1>

      <section>
        <h2>Histórico</h2>

        {loading && <p>Carregando...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}

        {!loading && !error && logs.length === 0 && (
          <p>Nenhuma mudança registrada ainda.</p>
        )}

        {!loading && !error && logs.length > 0 && (
          <ul style={{ listStyle: "none", padding: 0 }}>
            {logs.map((log) => (
              <li
                key={log.id_chage_log}
                style={{
                  border: "1px solid #eee",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                    alignItems: "baseline",
                  }}
                >
                  <strong>{log.titulo}</strong>
                  {log.versao && (
                    <span style={{ fontSize: 12, opacity: 0.7 }}>
                      Versão {log.version}
                    </span>
                  )}
                </div>

                <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                  {log.descricao}
                </p>

                {log.created_at && (
                  <small style={{ color: "#666" }}>
                    Criado em:{" "}
                    {new Date(log.created_at).toLocaleString("pt-BR")}
                  </small>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
*/

// src/pages/ChangeLogPage.jsx
import { useContext, useEffect, useState } from "react";
import { ServerContext } from "../../common/context/ServerContext";

// Material UI
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  Divider,
  Stack,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

export default function ChangeLogPage() {
  const { servidor } = useContext(ServerContext); // ex: http://localhost:8000/api

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function fetchLogs() {
    try {
      setLoading(true);
      setError("");

      // se servidor = http://localhost:8000/api
      // então endpoint = /changelogs/1
      const response = await fetch(`${servidor}/api/changelogs/1`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      setLogs(data);
    } catch (err) {
      console.error(err);
      setError("Erro ao carregar o changelog.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchLogs();
  }, [servidor]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("pt-BR");
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Cabeçalho */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryIcon fontSize="large" />
          <Box>
            <Typography variant="h4" component="h1" fontWeight={600}>
              Change Log
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Acompanhe as últimas alterações do sistema
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Estados de carregamento / erro */}
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          my={4}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box my={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && logs.length === 0 && (
        <Box my={4}>
          <Alert severity="info">Nenhuma mudança registrada ainda.</Alert>
        </Box>
      )}

      {/* Lista de logs */}
      {!loading && !error && logs.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Histórico
            </Typography>

            <List disablePadding>
              {logs.map((log, index) => (
                <Box key={log.id_change_log ?? index}>
                  {index > 0 && <Divider sx={{ my: 1.5 }} />}

                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <Box width="100%">
                      {/* Título + versão */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                        gap={1}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ wordBreak: "break-word" }}
                        >
                          {log.titulo}
                        </Typography>

                        {log.versao && (
                          <Chip
                            label={`Versão ${log.versao}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {/* Descrição */}
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          whiteSpace: "pre-wrap",
                          mb: 1,
                        }}
                      >
                        {log.descricao}
                      </Typography>

                      {/* Data */}
                      {log.created_at && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Criado em: {formatDate(log.created_at)}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
